// NbsPicker — escolhe o código NBS da lista oficial do Asaas.
// Só aceita código ESCOLHIDO da lista (não digitado): assim vai pra NF
// exatamente o código que o próprio Asaas publica. A lista aparece embaixo do
// campo, no fluxo da página — dentro de modal com rolagem, dropdown flutuante
// fica cortado.

import { Search, X } from 'lucide-react';
import { useState } from 'react';

import { useNbsCodes } from '../../hooks/use-fiscal';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { NBS_MIN_SEARCH, type NbsCodeOption } from '../../lib/fiscal-api';
import { Input } from '../clients/form-primitives';

export interface NbsPickerProps {
  /** id do Field, pra o rótulo apontar pro controle. */
  id?: string;
  describedBy?: string | undefined;
  value: NbsCodeOption | null;
  onChange: (value: NbsCodeOption | null) => void;
}

export function NbsPicker({ id, describedBy, value, onChange }: NbsPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim(), 300);
  const results = useNbsCodes(search);

  if (value) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-[10px] border border-line bg-panel-2 p-3">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium tabular-nums text-ink">{value.code}</div>
          <div className="mt-[2px] text-[11px] leading-[1.4] text-muted-2">{value.description}</div>
        </div>
        <button
          id={id}
          type="button"
          aria-label="Trocar código NBS"
          onClick={() => {
            onChange(null);
            setQuery('');
          }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink focus-visible:bg-panel-3 focus-visible:text-ink focus-visible:outline-none"
        >
          <X size={14} strokeWidth={1.8} />
        </button>
      </div>
    );
  }

  const typed = query.trim();
  const showPanel = typed.length >= NBS_MIN_SEARCH;
  const waiting = typed !== search || results.isLoading;
  const items = results.data ?? [];

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search
          size={14}
          strokeWidth={2}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
          aria-hidden
        />
        <Input
          id={id}
          aria-describedby={describedBy}
          type="text"
          autoComplete="off"
          placeholder="Buscar por palavra ou código — ex: software, 1.1103"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {showPanel && (
        <div
          aria-live="polite"
          className="max-h-[240px] overflow-y-auto rounded-[10px] border border-line bg-panel"
        >
          {waiting ? (
            <p className="px-3 py-3 text-[12px] text-muted">Buscando…</p>
          ) : results.isError ? (
            // Texto fixo de propósito: qualquer falha (Asaas fora, rede, resposta
            // estranha) tem a mesma saída pro operador — seguir sem NBS.
            <p className="px-3 py-3 text-[12px] text-red-300/80">
              Não conseguimos buscar os códigos agora. Você pode emitir sem NBS e tentar de novo
              depois.
            </p>
          ) : items.length === 0 ? (
            <p className="px-3 py-3 text-[12px] leading-[1.4] text-muted">
              Nenhum código encontrado. Tente uma palavra (ex: software) ou o código com pontos
              (ex: 1.1103).
            </p>
          ) : (
            <ul aria-label="Códigos NBS encontrados" className="py-1">
              {items.map((item) => (
                <li key={item.code}>
                  <button
                    type="button"
                    onClick={() => onChange(item)}
                    className="flex w-full flex-col items-start gap-[2px] px-3 py-2 text-left transition-colors hover:bg-panel-2 focus-visible:bg-panel-2 focus-visible:outline-none"
                  >
                    <span className="text-[12px] font-medium tabular-nums text-ink">
                      {item.code}
                    </span>
                    <span className="text-[11px] leading-[1.4] text-muted-2">
                      {item.description}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
